'use client';

/**
 * JsonRenderer Component
 *
 * Renders JSON field type with syntax highlighting.
 * Supports display, edit, and inline modes.
 */

import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import { alpha, styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { baseColors, getNeonStyles, type NeonAccent } from '../../../theme/palette';
import { transitionPresets } from '../../../theme/transitions';
import type { FieldDefinition, FieldMode } from '../../../types/entityFramework';
import { isValidJSON } from '../../../utils/validators';

// ============================================================================
// Types
// ============================================================================

export interface JsonRendererProps {
  /** Field definition */
  field: FieldDefinition;
  /** Current value (object or string) */
  value: unknown;
  /** Render mode */
  mode: FieldMode;
  /** Change handler */
  onChange?: (value: unknown) => void;
  /** Error message */
  error?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Size variant */
  size?: 'small' | 'medium' | 'large';
  /** Neon accent color */
  neonAccent?: NeonAccent;
  /** Additional class name */
  className?: string;
  // JSON-specific props
  /** Maximum height for the JSON container */
  maxHeight?: number | string;
  /** Whether to show collapsed initially */
  collapsed?: boolean;
}

// ============================================================================
// Syntax Highlighting Colors
// ============================================================================

interface SyntaxColors {
  key: string;
  string: string;
  number: string;
  boolean: string;
  null: string;
  bracket: string;
}

const getSyntaxColors = (isDark: boolean): SyntaxColors => ({
  key: isDark ? baseColors.secondary[400] : baseColors.secondary[600], // Cyan accent
  string: isDark ? baseColors.success[400] : baseColors.success[600], // Green
  number: isDark ? baseColors.warning[400] : baseColors.warning[600], // Amber
  boolean: isDark ? baseColors.warning[400] : baseColors.warning[600], // Amber
  null: isDark ? baseColors.neutral[400] : baseColors.neutral[500], // Slate (muted)
  bracket: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
});

// ============================================================================
// Styled Components
// ============================================================================

interface JsonContainerProps {
  $isDark: boolean;
  $neonAccent?: NeonAccent;
  $maxHeight?: number | string;
}

const JsonDisplayContainer = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<JsonContainerProps>(({ $isDark, $neonAccent, $maxHeight }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  return {
    fontFamily: '"JetBrains Mono", "Fira Code", "SF Mono", Consolas, monospace',
    fontSize: '0.875rem',
    lineHeight: 1.6,
    padding: '12px 16px',
    borderRadius: 12,
    backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
    border: `1px solid ${neonStyles ? neonStyles.border : $isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
    overflowX: 'auto',
    overflowY: 'auto',
    maxHeight: $maxHeight || '300px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  };
});

interface StyledTextareaProps {
  $isDark: boolean;
  $neonAccent?: NeonAccent;
  $hasError: boolean;
  $size: 'small' | 'medium' | 'large';
}

const StyledTextarea = styled('textarea', {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledTextareaProps>(({ theme, $isDark, $neonAccent, $hasError, $size }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  const sizeStyles = {
    small: { fontSize: '0.8125rem', padding: '8px 12px', minHeight: '120px' },
    medium: { fontSize: '0.875rem', padding: '12px 16px', minHeight: '160px' },
    large: { fontSize: '0.9375rem', padding: '16px 20px', minHeight: '200px' },
  };

  const s = sizeStyles[$size];

  return {
    width: '100%',
    fontFamily: '"JetBrains Mono", "Fira Code", "SF Mono", Consolas, monospace',
    fontSize: s.fontSize,
    lineHeight: 1.6,
    padding: s.padding,
    minHeight: s.minHeight,
    borderRadius: 12,
    border: `1px solid ${
      $hasError
        ? theme.palette.error.main
        : neonStyles
          ? neonStyles.border
          : $isDark
            ? 'rgba(255, 255, 255, 0.15)'
            : 'rgba(0, 0, 0, 0.15)'
    }`,
    backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
    color: $isDark ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.87)',
    resize: 'vertical',
    outline: 'none',
    transition: transitionPresets.input,

    '&::placeholder': {
      color: $isDark ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
    },

    '&:hover': {
      borderColor: $hasError ? theme.palette.error.main : neonStyles ? neonStyles.base : theme.palette.primary.main,
    },

    '&:focus': {
      borderColor: $hasError ? theme.palette.error.main : neonStyles ? neonStyles.base : theme.palette.primary.main,
      borderWidth: 2,
      boxShadow: neonStyles ? `0 0 12px ${neonStyles.glow}` : undefined,
      backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.01)',
    },

    '&:disabled': {
      backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.04)',
      cursor: 'not-allowed',
      opacity: 0.6,
    },
  };
});

const InlineTextarea = styled('textarea', {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{
  $isDark: boolean;
  $neonAccent?: NeonAccent;
}>(({ theme, $isDark, $neonAccent }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  return {
    width: '100%',
    fontFamily: '"JetBrains Mono", "Fira Code", "SF Mono", Consolas, monospace',
    fontSize: '0.8125rem',
    lineHeight: 1.5,
    padding: '4px 8px',
    minHeight: '60px',
    borderRadius: 8,
    border: '1px solid transparent',
    backgroundColor: 'transparent',
    color: $isDark ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.87)',
    resize: 'vertical',
    outline: 'none',
    transition: transitionPresets.input,

    '&::placeholder': {
      color: $isDark ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
    },

    '&:hover': {
      borderColor: neonStyles
        ? alpha(neonStyles.border, 0.5)
        : $isDark
          ? 'rgba(255, 255, 255, 0.1)'
          : 'rgba(0, 0, 0, 0.1)',
    },

    '&:focus': {
      backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
      borderColor: neonStyles ? neonStyles.base : theme.palette.primary.main,
    },

    '&:disabled': {
      cursor: 'not-allowed',
      opacity: 0.6,
    },
  };
});

const StyledFormLabel = styled(FormLabel, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $isDark: boolean }>(({ $isDark }) => ({
  color: $isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
  fontSize: '0.875rem',
  marginBottom: 6,
  display: 'block',
}));

const StyledFormHelperText = styled(FormHelperText)(() => ({
  marginLeft: 4,
  marginTop: 6,
}));

const DisplayValue = styled(Typography, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $isDark: boolean }>(({ $isDark }) => ({
  color: $isDark ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.87)',
  wordBreak: 'break-word',
}));

// ============================================================================
// Syntax Highlighting Helper
// ============================================================================

interface HighlightedToken {
  type: 'key' | 'string' | 'number' | 'boolean' | 'null' | 'bracket' | 'other';
  value: string;
}

/**
 * Pre-compiled regex patterns for JSON syntax highlighting
 * Moved to module level to avoid repeated compilation overhead
 */
const JSON_SYNTAX_PATTERNS = [
  { type: 'string' as const, source: '"(?:[^"\\\\]|\\\\.)*"' },
  { type: 'number' as const, source: '-?\\d+(?:\\.\\d+)?(?:[eE][+-]?\\d+)?' },
  { type: 'boolean' as const, source: '\\b(?:true|false)\\b' },
  { type: 'null' as const, source: '\\bnull\\b' },
  { type: 'bracket' as const, source: '[\\[\\]{}:,]' },
] as const;

/**
 * Simple JSON syntax highlighter that returns styled spans
 */
function highlightJSON(jsonString: string, colors: SyntaxColors): React.ReactNode[] {
  const tokens: HighlightedToken[] = [];

  // Find all matches with positions using pre-compiled pattern sources
  const matches: Array<{
    type: HighlightedToken['type'];
    value: string;
    index: number;
  }> = [];

  for (const pattern of JSON_SYNTAX_PATTERNS) {
    let match: RegExpExecArray | null;
    const regex = new RegExp(pattern.source, 'g');
    while ((match = regex.exec(jsonString)) !== null) {
      matches.push({
        type: pattern.type,
        value: match[0],
        index: match.index,
      });
    }
  }

  // Sort by position
  matches.sort((a, b) => a.index - b.index);

  // Build tokens, filling gaps with 'other' type
  let currentIndex = 0;
  for (const match of matches) {
    if (match.index > currentIndex) {
      tokens.push({
        type: 'other',
        value: jsonString.slice(currentIndex, match.index),
      });
    }
    // Check if this is a key (string followed by colon)
    if (match.type === 'string') {
      const afterMatch = jsonString.slice(match.index + match.value.length);
      const isKey = /^\s*:/.test(afterMatch);
      tokens.push({
        type: isKey ? 'key' : 'string',
        value: match.value,
      });
    } else {
      tokens.push({
        type: match.type,
        value: match.value,
      });
    }
    currentIndex = match.index + match.value.length;
  }

  // Add remaining text
  if (currentIndex < jsonString.length) {
    tokens.push({
      type: 'other',
      value: jsonString.slice(currentIndex),
    });
  }

  // Convert tokens to React elements
  return tokens.map((token, index) => {
    const color = token.type === 'other' ? undefined : colors[token.type as keyof SyntaxColors];

    return (
      <span key={index} style={color ? { color } : undefined}>
        {token.value}
      </span>
    );
  });
}

// ============================================================================
// Component
// ============================================================================

export const JsonRenderer: React.FC<JsonRendererProps> = ({
  field,
  value,
  mode,
  onChange,
  error,
  disabled = false,
  size = 'medium',
  neonAccent,
  className,
  maxHeight,
  collapsed = false,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const syntaxColors = getSyntaxColors(isDark);

  // Track local validation error
  const [localError, setLocalError] = useState<string | null>(null);

  // Convert value to string for editing/display
  const stringValue = useMemo(() => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return '';
    }
  }, [value]);

  // Format JSON for display with syntax highlighting
  const formattedContent = useMemo(() => {
    if (!stringValue) return null;

    let jsonString: string;
    try {
      // Parse and re-stringify to ensure proper formatting
      const parsed = JSON.parse(stringValue);
      // Use collapsed mode (no indentation) or expanded (2-space indent)
      jsonString = JSON.stringify(parsed, null, collapsed ? 0 : 2);
    } catch {
      // If invalid JSON, just display as-is without highlighting
      return stringValue;
    }

    return highlightJSON(jsonString, syntaxColors);
  }, [stringValue, syntaxColors, collapsed]);

  // Handle value change with validation
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;

      // Clear local error on change
      setLocalError(null);

      // If empty, pass null
      if (!newValue.trim()) {
        onChange?.(null);
        return;
      }

      // Validate JSON
      if (!isValidJSON(newValue)) {
        setLocalError('Invalid JSON format');
        // Still pass the string value so user can continue editing
        onChange?.(newValue);
        return;
      }

      // Parse and pass the object
      try {
        const parsed = JSON.parse(newValue);
        onChange?.(parsed);
      } catch {
        setLocalError('Invalid JSON format');
        onChange?.(newValue);
      }
    },
    [onChange],
  );

  // Determine display error
  const displayError = error || localError;

  // Display mode
  if (mode === 'display') {
    if (!stringValue) {
      return (
        <Box className={className}>
          <DisplayValue variant="body2" color="textSecondary" $isDark={isDark}>
            -
          </DisplayValue>
        </Box>
      );
    }

    return (
      <Box className={className}>
        <JsonDisplayContainer $isDark={isDark} $neonAccent={neonAccent} $maxHeight={maxHeight}>
          {formattedContent}
        </JsonDisplayContainer>
      </Box>
    );
  }

  // Inline mode
  if (mode === 'inline') {
    return (
      <InlineTextarea
        value={stringValue}
        onChange={handleChange}
        placeholder={field.placeholder || '{}'}
        disabled={disabled || field.disabled}
        $isDark={isDark}
        $neonAccent={neonAccent}
        className={className}
      />
    );
  }

  // Edit mode
  return (
    <Box className={className}>
      {field.label && (
        <StyledFormLabel $isDark={isDark} required={field.required}>
          {field.label}
        </StyledFormLabel>
      )}
      <StyledTextarea
        value={stringValue}
        onChange={handleChange}
        placeholder={field.placeholder || '{\n  "key": "value"\n}'}
        disabled={disabled || field.disabled}
        $isDark={isDark}
        $neonAccent={neonAccent}
        $hasError={!!displayError}
        $size={size}
      />
      {(displayError || field.helpText) && (
        <StyledFormHelperText error={!!displayError}>{displayError || field.helpText}</StyledFormHelperText>
      )}
    </Box>
  );
};

JsonRenderer.displayName = 'JsonRenderer';

export default JsonRenderer;
